import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Users, Zap, Shield, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');

  const features = [
    t('pricing.features.voiceChat'),
    t('pricing.features.crmUpdates'),
    t('pricing.features.integrations'),
    t('pricing.features.anyField'),
    t('pricing.features.gdpr'),
    t('pricing.features.oauth'),
    t('pricing.features.emailSupport'),
    t('pricing.features.teamCollab'),
  ];

  const volumeBenefits = [
    { users: t('pricing.volume.tier1.users'), benefit: t('pricing.volume.tier1.benefit') },
    { users: t('pricing.volume.tier2.users'), benefit: t('pricing.volume.tier2.benefit') },
    { users: t('pricing.volume.tier3.users'), benefit: t('pricing.volume.tier3.benefit') },
  ];

  const scrollToCTA = () => {
    const trialPath = isFrench ? '/fr/essai' : '/trial';
    navigate(trialPath);
  };

  return (
    <Layout>
      <SEOHead
        title={t('pricing.seo.title')}
        description={t('pricing.seo.description')}
        canonicalUrl="https://noq5.ai/pricing"
        keywords="NoQ5.ai pricing, HubSpot AI pricing, CRM automation pricing, voice AI for sales pricing, revenue intelligence pricing"
      />
      
      <Breadcrumbs items={[{ label: "Pricing", href: "/pricing" }]} />

      <div className="py-12 md:py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 px-4"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            {t('pricing.header.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            {t('pricing.header.subtitle')}
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-700 rounded-full">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-medium">{t('pricing.header.trialBadge')}</span>
          </div>
        </motion.div>

        {/* Pain Points Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="max-w-4xl mx-auto px-4 mb-16"
        >
          <div className="bg-muted/30 border border-border rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-foreground text-center mb-6">
              {t('pricing.painPoints.title')}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl mb-2">😤</div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t('pricing.painPoints.point1.title')}</span><br/>
                  {t('pricing.painPoints.point1.subtitle')}
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">📉</div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t('pricing.painPoints.point2.title')}</span><br/>
                  {t('pricing.painPoints.point2.subtitle')}
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">⏰</div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t('pricing.painPoints.point3.title')}</span><br/>
                  {t('pricing.painPoints.point3.subtitle')}
                </p>
              </div>
            </div>
            <p className="text-center mt-6 text-oli-blue font-medium">
              {t('pricing.painPoints.solution')}
            </p>
          </div>
        </motion.div>

        <div className="max-w-xl mx-auto px-4 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-2 border-oli-blue shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-oli-blue to-oli-blue-dark" />
              <CardHeader className="text-center pt-8">
                <CardTitle className="text-3xl font-bold">NoQ5.ai</CardTitle>
                <CardDescription className="text-base">{t('pricing.card.description')}</CardDescription>
                <div className="mt-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-foreground">€129</span>
                    <span className="text-xl text-muted-foreground">{t('pricing.card.perUserMonth')}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('pricing.card.commitment')}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="px-8">
                <ul className="space-y-4">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-oli-blue flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex flex-col gap-4 px-8 pb-8">
                <Button 
                  className="w-full bg-oli-blue hover:bg-oli-blue-dark text-white text-lg py-6"
                  onClick={scrollToCTA}
                >
                  {t('pricing.card.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  {t('pricing.card.noCreditCard')}
                </p>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Team Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-3xl mx-auto px-4 mb-20"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
              <Users className="w-6 h-6 text-oli-blue" />
              {t('pricing.teams.title')}
            </h2>
            <p className="text-muted-foreground">{t('pricing.teams.subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {volumeBenefits.map((tier, index) => (
              <div 
                key={index} 
                className="bg-card border border-border rounded-xl p-5 text-center hover:border-oli-blue/50 transition-colors"
              >
                <p className="font-semibold text-foreground mb-1">{tier.users}</p>
                <p className="text-sm text-muted-foreground">{tier.benefit}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-6">
            <a href="mailto:hello@noq5.ai" className="text-oli-blue hover:underline font-medium">{t('pricing.teams.contact')}</a> {t('pricing.teams.contactSuffix')}
          </p>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto px-4 mb-20"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl">
              <Shield className="w-10 h-10 text-oli-blue flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">{t('pricing.trust.gdpr.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('pricing.trust.gdpr.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl">
              <Clock className="w-10 h-10 text-oli-blue flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">{t('pricing.trust.setup.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('pricing.trust.setup.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-xl">
              <Zap className="w-10 h-10 text-oli-blue flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">{t('pricing.trust.save.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('pricing.trust.save.subtitle')}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-3xl mx-auto px-4"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground mb-4">
              {t('pricing.faq.title')}
            </h2>
          </div>
          
          <div className="space-y-6">
            <Card className="border">
              <CardHeader>
                <CardTitle className="text-lg">{t('pricing.faq.questions.whatsIncluded.question')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('pricing.faq.answers.whatsIncluded')}</p>
              </CardContent>
            </Card>

            <Card className="border">
              <CardHeader>
                <CardTitle className="text-lg">{t('pricing.faq.questions.trialPeriod.question')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('pricing.faq.questions.trialPeriod.answer')}</p>
              </CardContent>
            </Card>

            <Card className="border">
              <CardHeader>
                <CardTitle className="text-lg">{t('pricing.faq.questions.contract.question')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('pricing.faq.questions.contract.answer')}</p>
              </CardContent>
            </Card>

            <Card className="border">
              <CardHeader>
                <CardTitle className="text-lg">{t('pricing.faq.questions.paymentMethods.question')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('pricing.faq.questions.paymentMethods.answer')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-16 text-center"
          >
            <div className="bg-oli-blue rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t('pricing.finalCta.title')}
              </h3>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                {t('pricing.finalCta.subtitle')}
              </p>
              <Button 
                size="lg"
                className="bg-white text-oli-blue hover:bg-white/90 font-semibold px-8 py-6 text-lg"
                onClick={scrollToCTA}
              >
                {t('pricing.card.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
}